import React from 'react';
import { LogInOutline, LogOutOutline } from 'react-ionicons';
import { Link, useLocation } from 'react-router-dom';

import { featureFlags } from '../../config/featureFlags';
import { useAuth } from '../../contexts/AuthContext';
import { useAuthPrompt } from '../../contexts/AuthPromptContext';
import { FILLS, FONTS, SIZING, STROKES } from '../../styles/tokens';
import { isPlainGuestLinkClick } from '../../utils/postLoginRedirect';
import { Sheet, SheetTitle } from '../common/Sheet';
import { CloseIcon } from '../icons';
import {
  MenuItem,
  aboutMenuItem,
  adminPanelMenuItem,
  belhopPlusMenuItem,
  bookingsMenuItem,
  conciergeEnquiryMenuItem,
  requestsMenuItem,
  wishlistMenuItem,
} from './MenuItems';

interface MobileMenuSheetProps {
  /** Whether the sheet is visible. Renders nothing when false. */
  open: boolean;
  /** Closes the sheet. */
  onClose: () => void;
  /** Opens the shared logout confirmation dialog (owned by Header). */
  onLogoutClick: () => void;
}

const ICON_SIZE = '20px';

const closeButtonStyle: React.CSSProperties = {
  minWidth: SIZING.touchTarget,
  minHeight: SIZING.touchTarget,
  background: 'none',
  border: 'none',
  color: FILLS.textLight,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  padding: 0,
};

const linkButtonStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  width: '100%',
  padding: '14px 20px',
  background: 'none',
  border: 'none',
  color: FILLS.textPrimary,
  cursor: 'pointer',
  fontSize: '16px',
  fontFamily: FONTS.body,
  textAlign: 'left',
};

const authActionStyle: React.CSSProperties = {
  ...linkButtonStyle,
  gap: '12px',
};

/**
 * Secondary / overflow navigation sheet opened from the mobile app-bar menu
 * button. Primary destinations (Discover/Concierge/Log in or Profile) live in
 * the bottom tab bar, so this sheet only surfaces overflow links (Bookings,
 * Requests, Concierge, Belhop+, About), Wishlist for everyone while its flag
 * is on (a guest is sent through login to it), role-gated entries (List
 * Experience / Join Partners / Admin Panel) and the auth action (Sign in /
 * Sign out). Concierge, Belhop+ and About are always last and in that order
 * for every guest, signed in or not. Built on the shared phone `Sheet`.
 */
const MobileMenuSheet: React.FC<MobileMenuSheetProps> = ({ open, onClose, onLogoutClick }) => {
  const location = useLocation();
  const { isAuthenticated, user } = useAuth();
  const { openAuthPrompt } = useAuthPrompt();

  if (!open) return null;

  // Overflow + role-gated links. Mirrors the old drawer's gating exactly, minus
  // the primary destinations now owned by the bottom nav.
  const menuItems: MenuItem[] = [
    ...(isAuthenticated ? [bookingsMenuItem] : []),
    ...(isAuthenticated ? [requestsMenuItem] : []),
    ...(featureFlags.wishlist ? [wishlistMenuItem] : []),
    ...(user?.role === 'admin' && isAuthenticated ? [adminPanelMenuItem] : []),
    conciergeEnquiryMenuItem,
    belhopPlusMenuItem,
    aboutMenuItem,
  ];

  const handleSignOut = () => {
    onClose();
    onLogoutClick();
  };

  // A plain click closes the menu first, then opens the pop-up, so two overlays are never open together.
  const handleSignInClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    onClose();
    if (!isPlainGuestLinkClick(event, location.pathname)) return;
    event.preventDefault();
    openAuthPrompt({ intent: 'signIn' });
  };

  return (
    <Sheet layout="bottom" onClose={onClose} backdropTestId="menu-sheet-backdrop">
      {/* Header */}
      <div
        style={{
          padding: '16px 16px 12px',
          borderBottom: `1px solid ${STROKES.subtle}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
        }}
      >
        <SheetTitle
          style={{
            fontFamily: FONTS.heading,
            fontSize: '18px',
            fontWeight: 600,
            color: FILLS.textPrimary,
            margin: 0,
          }}
        >
          Menu
        </SheetTitle>
        <button type="button" onClick={onClose} aria-label="Close menu" style={closeButtonStyle}>
          <CloseIcon width="20px" height="20px" />
        </button>
      </div>

      {/* Links */}
      <nav style={{ padding: '8px 0' }}>
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {menuItems.map((item) => {
            const isActive = location.pathname.startsWith(item.path);
            return (
              <li key={item.text}>
                <Link
                  to={item.path}
                  state={item.state}
                  onClick={onClose}
                  style={{
                    ...linkButtonStyle,
                    color: isActive ? FILLS.gold : FILLS.textPrimary,
                    textDecoration: 'none',
                  }}
                >
                  <span style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                    {item.icon(ICON_SIZE, isActive ? FILLS.gold : FILLS.textPrimary)}
                  </span>
                  {item.text}
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Auth action */}
      <div style={{ borderTop: `1px solid ${STROKES.subtle}`, padding: '8px 0' }}>
        {isAuthenticated ? (
          <button type="button" onClick={handleSignOut} style={authActionStyle}>
            <LogOutOutline width={ICON_SIZE} height={ICON_SIZE} color={FILLS.textPrimary} />
            Sign out
          </button>
        ) : (
          <Link
            to="/login"
            onClick={handleSignInClick}
            style={{ ...authActionStyle, textDecoration: 'none' }}
          >
            <LogInOutline width={ICON_SIZE} height={ICON_SIZE} color={FILLS.textPrimary} />
            Sign in
          </Link>
        )}
      </div>
    </Sheet>
  );
};

export default MobileMenuSheet;
